/**
 * Synology NAS List Page
 * =======================
 * Browse and search Synology NAS assets with filtering.
 * Filtered to NAS asset type and uses Synology-specific IP lookup via SSH.
 */

import { useState, useEffect, useRef } from 'react'
import { Link, useSearchParams as useRouterSearchParams, useNavigate } from 'react-router-dom'
import { Plus, Search, HardDrive, ChevronLeft, ChevronRight, Network, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import AddByIPModal from '@/components/AddByIPModal'
import { useAssets } from '@/hooks/useAssets'
import { useBatchResync } from '@/hooks/useBatchResync'
import { useAssetLocations } from '@/hooks/useAssetLocations'
import { useAssetManufacturers } from '@/hooks/useAssetManufacturers'
import type { AssetSearchParams } from '@/types'
import { ASSET_STATUS_COLORS, ASSET_STATUS_LABELS } from '@/types'
import { formatStorageCapacity } from '@/utils/storage'

export default function SynologyList() {
  const navigate = useNavigate()
  const [routerSearchParams] = useRouterSearchParams()
  const initialSearch = routerSearchParams.get('search') || ''
  const initialLocationId = routerSearchParams.get('location_id') || ''
  const initialManufacturerId = routerSearchParams.get('manufacturer_id') || ''

  // Always filter to NAS type for Synology devices
  const [searchParams, setSearchParams] = useState<AssetSearchParams>({
    page: 1,
    limit: 20,
    search: initialSearch,
    asset_type: 'nas',  // Fixed filter for Synology NAS
    location_id: initialLocationId ? Number(initialLocationId) : undefined,
    manufacturer_id: initialManufacturerId ? Number(initialManufacturerId) : undefined,
  })

  // Modal state
  const [showAddByIPModal, setShowAddByIPModal] = useState(false)

  // Autocomplete state
  const [searchInput, setSearchInput] = useState(initialSearch)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const searchRef = useRef<HTMLDivElement>(null)

  // Update search when URL changes
  useEffect(() => {
    const urlSearch = routerSearchParams.get('search') || ''
    const urlLocationId = routerSearchParams.get('location_id') || ''
    const urlManufacturerId = routerSearchParams.get('manufacturer_id') || ''

    setSearchParams((prev) => ({
      ...prev,
      search: urlSearch,
      location_id: urlLocationId ? Number(urlLocationId) : undefined,
      manufacturer_id: urlManufacturerId ? Number(urlManufacturerId) : undefined,
      page: 1,
      asset_type: 'nas',  // Always maintain NAS filter
    }))
    setSearchInput(urlSearch)
  }, [routerSearchParams])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const { data, isLoading, error } = useAssets(searchParams)
  const { data: locationsData } = useAssetLocations()
  const { data: manufacturersData } = useAssetManufacturers()

  // Autocomplete search results (filtered to NAS)
  const searchTerm = searchInput.trim()
  const { data: autocompleteResults, isLoading: autocompleteLoading } = useAssets(
    { search: searchTerm, limit: 8, asset_type: 'nas' },
    { enabled: searchTerm.length >= 2 }
  )
  const suggestions = searchTerm.length >= 2 ? (autocompleteResults?.data ?? []) : []

  const assets = data?.data ?? []
  const meta = data?.meta
  const locations = locationsData?.data ?? []
  const manufacturers = manufacturersData?.data ?? []

  // Batch resync: auto-sync all assets on page load
  const { syncingIds, isSyncing, completedCount, totalCount, startBatchResync } = useBatchResync(searchParams)
  const hasTriggeredResyncRef = useRef(false)
  useEffect(() => {
    if (!isLoading && assets.length > 0 && !hasTriggeredResyncRef.current) {
      hasTriggeredResyncRef.current = true
      startBatchResync(assets)
    }
  }, [isLoading, assets.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setShowDropdown(false)
    setSearchParams((prev) => ({
      ...prev,
      search: searchInput,
      page: 1,
    }))
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
    setShowDropdown(true)
    setSelectedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault()
      const asset = suggestions[selectedIndex]
      setShowDropdown(false)
      navigate(`/assets/${asset.asset_id}`)
    } else if (e.key === 'Escape') {
      setShowDropdown(false)
    }
  }

  const handleSuggestionClick = (assetId: string) => {
    setShowDropdown(false)
    navigate(`/assets/${assetId}`)
  }

  const handleFilterChange = (key: keyof AssetSearchParams, value: string) => {
    setSearchParams((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }))
  }

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => ({ ...prev, page: newPage }))
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error loading assets</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <HardDrive className="text-purple-500" size={28} />
            Synology NAS
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {meta?.total ?? 0} NAS devices tracked
          </p>
          {isSyncing && (
            <p className="text-sm text-blue-600 dark:text-blue-400 flex items-center gap-1.5 mt-0.5">
              <Loader2 size={14} className="animate-spin" />
              Syncing {completedCount}/{totalCount} assets...
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={<Network size={16} />}
            onClick={() => setShowAddByIPModal(true)}
          >
            Add by IP
          </Button>
          <Link to="/assets/new">
            <Button icon={<Plus size={16} />}>Add Manually</Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]" ref={searchRef}>
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  name="search"
                  type="text"
                  placeholder="Search by hostname, IP address, serial..."
                  value={searchInput}
                  onChange={handleInputChange}
                  onFocus={() => setShowDropdown(true)}
                  onKeyDown={handleKeyDown}
                  className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                />
                {autocompleteLoading && searchTerm.length >= 2 && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}

                {/* Autocomplete dropdown */}
                {showDropdown && searchTerm.length >= 2 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                    {suggestions.length === 0 && !autocompleteLoading ? (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        No NAS devices found for "{searchInput}"
                      </div>
                    ) : (
                      suggestions.map((asset, index) => (
                        <button
                          key={asset.id}
                          type="button"
                          onClick={() => handleSuggestionClick(asset.asset_id)}
                          className={`w-full px-4 py-2 flex items-center gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            index === selectedIndex ? 'bg-primary-50 dark:bg-primary-900/50' : ''
                          } ${index !== suggestions.length - 1 ? 'border-b dark:border-gray-700' : ''}`}
                        >
                          <div className="w-8 h-8 rounded bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center flex-shrink-0">
                            <HardDrive className="text-purple-500" size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {asset.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {asset.asset_id}
                              {asset.ip_address && ` • ${asset.ip_address}`}
                            </p>
                          </div>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${ASSET_STATUS_COLORS[asset.status]}`}>
                            {ASSET_STATUS_LABELS[asset.status]}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <select
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={searchParams.status ?? ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Status</option>
              {Object.entries(ASSET_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={searchParams.location_id ?? ''}
              onChange={(e) => handleFilterChange('location_id', e.target.value)}
            >
              <option value="">All Locations</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
            <select
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={searchParams.manufacturer_id ?? ''}
              onChange={(e) => handleFilterChange('manufacturer_id', e.target.value)}
            >
              <option value="">All Manufacturers</option>
              {manufacturers.map((mfr) => (
                <option key={mfr.id} value={mfr.id}>
                  {mfr.name}
                </option>
              ))}
            </select>
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Assets list */}
      <Card>
        <CardHeader title="Synology NAS Devices" />
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  NAS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Storage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Network
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  DSM
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : assets.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <HardDrive className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={48} />
                    <p className="text-gray-500 dark:text-gray-400">No Synology NAS devices found</p>
                    <button
                      onClick={() => setShowAddByIPModal(true)}
                      className="text-primary-600 dark:text-primary-400 hover:underline mt-2 inline-block"
                    >
                      Add a NAS by IP
                    </button>
                  </td>
                </tr>
              ) : (
                assets.map((asset) => (
                  <tr key={asset.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/assets/${asset.asset_id}`}
                        className="flex items-center gap-3"
                      >
                        {asset.manufacturer?.logo_url ? (
                          <img
                            src={asset.manufacturer.logo_url}
                            alt={asset.manufacturer.name}
                            className="w-10 h-10 rounded-lg object-contain"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                            <HardDrive className="text-purple-500" size={20} />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {asset.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {asset.model || 'Synology NAS'}
                          </p>
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-mono text-gray-900 dark:text-white">
                        {asset.asset_id}
                      </p>
                      {asset.service_tag && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {asset.service_tag}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white space-y-0.5">
                        {asset.storage_capacity_gb && (
                          <p>{formatStorageCapacity(asset.storage_capacity_gb)}</p>
                        )}
                        {asset.storage_count && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {asset.storage_count} drives
                            {asset.storage_type && ` (${asset.storage_type})`}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {asset.ip_address ? (
                        <p className="text-sm font-mono text-gray-900 dark:text-white">
                          {asset.ip_address}
                        </p>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                      {asset.mac_address && (
                        <p className="text-xs font-mono text-gray-500 dark:text-gray-400">
                          {asset.mac_address}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {asset.dsm_update_available === true ? (
                        <span
                          className="inline-flex items-center"
                          title={(() => {
                            if (!asset.dsm_update_info) return 'Update available'
                            try {
                              const info = JSON.parse(asset.dsm_update_info)
                              const parts: string[] = []
                              if (info.update?.available && info.update?.version) {
                                parts.push(`System: ${info.update.version}`)
                              }
                              if (info.security?.available && info.security?.version) {
                                parts.push(`Security: ${info.security.version}`)
                              }
                              return parts.join('\n') || 'Update available'
                            } catch { return 'Update available' }
                          })()}
                        >
                          <span className="w-2.5 h-2.5 bg-red-500 rounded-full"></span>
                        </span>
                      ) : asset.dsm_update_available === false ? (
                        <span className="inline-flex items-center" title="DSM is up to date">
                          <span className="w-2.5 h-2.5 bg-green-500 rounded-full"></span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center" title="Update status unknown">
                          <span className="w-2.5 h-2.5 bg-gray-400 rounded-full"></span>
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {syncingIds.has(asset.id) ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                          <Loader2 size={12} className="animate-spin" />
                          Syncing
                        </span>
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${ASSET_STATUS_COLORS[asset.status]}`}>
                          {ASSET_STATUS_LABELS[asset.status]}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta && meta.total_pages > 1 && (
          <div className="px-6 py-4 border-t dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {(meta.page - 1) * meta.limit + 1} to{' '}
              {Math.min(meta.page * meta.limit, meta.total)} of {meta.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_prev}
                onClick={() => handlePageChange(meta.page - 1)}
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_next}
                onClick={() => handlePageChange(meta.page + 1)}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Add by IP Modal - restricted to Synology */}
      <AddByIPModal
        isOpen={showAddByIPModal}
        onClose={() => setShowAddByIPModal(false)}
        source="synology"
        returnPath="/assets/synology"
      />
    </div>
  )
}
